import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { gsap } from 'gsap';

/**
 * useSmoothScroll Hook
 * Provides smooth scroll animation using GSAP when route changes
 */
const useSmoothScroll = () => {
  const location = useLocation();
  const scrollTweenRef = useRef(null);

  useEffect(() => {
    // Kill any existing scroll animation
    if (scrollTweenRef.current) {
      scrollTweenRef.current.kill();
    }

    // Get current scroll position
    const currentScroll = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;

    // Only animate if we're not already at the top
    if (currentScroll > 0) {
      // Create a proxy object for GSAP to animate
      const scrollProxy = { value: currentScroll };

      // Animate scroll position using GSAP
      scrollTweenRef.current = gsap.to(scrollProxy, {
        value: 0,
        duration: 0.8,
        ease: 'power2.out',
        onUpdate: () => {
          window.scrollTo(0, scrollProxy.value);
          document.documentElement.scrollTop = scrollProxy.value;
          document.body.scrollTop = scrollProxy.value;
        },
        onComplete: () => {
          // Ensure we're at the top (fallback)
          window.scrollTo(0, 0);
          document.body.scrollTop = 0;
          document.documentElement.scrollTop = 0;
        }
      });
    } else {
      // If already at top, just ensure it stays there
      window.scrollTo(0, 0);
      document.body.scrollTop = 0;
      document.documentElement.scrollTop = 0;
    }

    // Cleanup function
    return () => {
      if (scrollTweenRef.current) {
        scrollTweenRef.current.kill();
      }
    };
  }, [location.pathname]);
};

export default useSmoothScroll;
